import { ObjectGraph } from '@jet/environment/dependencies';

// Types
import type {
    ClassicalAPIResponse,
    ClassicalAPIResponseHeader,
} from '../../api/types';
import type { PlaylistPage } from '../../models/pages/playlist';

// Utils
import { playActionForClassicalPlayAction } from '../../action-model/PlayAction';
import { transformSections } from '@jet-app/music-classical/utilities/transforms/sections';
import {
    createPageMetricsForResponse,
    PageType,
} from '@jet-app/music-classical/metrics';
import {
    fixArtwork,
    makeVideoArtworkDictionary,
} from '@jet-app/music-classical/models';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import { addingPlaylistSeoData } from '@jet-app/music-classical/utilities/seo/Playlist';
import { transformUserComponentsToContextMenuItems } from '@jet-app/music-classical/intents/context-menu/transformContextMenuItems';
import { getLibraryPlaylistActionPayload } from '@jet-app/music-classical/utilities/transforms/playlist';
import type { ContextualMenuResponse } from '@jet-app/music-classical/utilities/should-render-contextual-menu';

export function renderPlaylistPage(
    canonicalURL: string,
    apiResponse: ClassicalAPIResponse,
    contextualMenuResponse: ContextualMenuResponse | undefined,
    objectGraph: ObjectGraph,
    prominentItemIdentifier?: string,
    includeSecondaryContextButtons: boolean = false,
): PlaylistPage {
    const responseHeader = apiResponse?.body
        ?.header as unknown as ClassicalAPIResponseHeader;

    const secondaryButtonsContextMenuAction = includeSecondaryContextButtons
        ? makeContextualMenuFetchAction(
              `/secondary-buttons/${responseHeader?.type}/${responseHeader?.title}`, // As Classical Server does not provide a URL, we hack to create a URL to store it in our LRU Cache for Context Menu Items
              transformUserComponentsToContextMenuItems(
                  (apiResponse.body?.secondaryButtons as any[]) || [],
                  objectGraph,
                  getLibraryPlaylistActionPayload(apiResponse),
              ),
          )
        : null;

    const page = {
        pageKind: 'componentScreen',
        kind: 'PlaylistPage',
        screenId: apiResponse?.body.screenId,
        screenType: apiResponse?.body.screenType,
        header: {
            id: apiResponse?.body.screenId,
            type: responseHeader?.type,
            title: responseHeader?.title,
            subtitle: responseHeader.subtitle,
            artwork: fixArtwork(responseHeader.image),
            videoArtwork: makeVideoArtworkDictionary(
                responseHeader.uberArtwork,
            ),
            modalPresentationDescriptor: !!responseHeader?.description?.standard
                ? {
                      headerTitle: responseHeader.description.title,
                      headerSubtitle: null, // TODO: See where this value would come from
                      paragraphText: responseHeader.description.standard,
                  }
                : null,
            lastUpdated: responseHeader?.lastUpdated,
            // rdar://150296128 (Album pages 500)
            // There is potential for primary button playParams to have a null value
            primaryButton:
                apiResponse.body.primaryButton?.action?.playParams?.filter(
                    (param) => !!param,
                )?.length
                    ? {
                          title: apiResponse.body.primaryButton?.title,
                          action: playActionForClassicalPlayAction(
                              apiResponse.body.primaryButton?.action,
                          ),
                      }
                    : null,
            contextMenuAction:
                apiResponse?.body?.userComponentsAction &&
                contextualMenuResponse?.renderContextualMenu
                    ? makeContextualMenuFetchAction(
                          apiResponse?.body?.userComponentsAction.url,
                      )
                    : secondaryButtonsContextMenuAction || null,
            shareButton:
                !apiResponse?.body?.userComponentsAction ||
                !contextualMenuResponse?.renderContextualMenu
                    ? apiResponse?.body?.secondaryButtons?.find(
                          (button) => button.subtype === 'share',
                      )
                    : null,
            entities: contextualMenuResponse?.entities,
        },
        sections:
            transformSections(
                apiResponse?.body?.sections as any,
                objectGraph,
                apiResponse?.body?.screenType,
                apiResponse?.body?.screenId,
                prominentItemIdentifier,
            ) || [],
        canonicalURL,
        pageMetrics: createPageMetricsForResponse(
            apiResponse,
            PageType.playlistPage,
        ),
    };

    return addingPlaylistSeoData(canonicalURL, page, objectGraph);
}
